import { memo, useState, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { Bot, User, Wrench, ChevronDown, ChevronUp } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from '@/lib/utils.ts'

const bubbleWrapperStyles = cva('flex gap-3 w-full', {
  variants: {
    role: {
      user: 'flex-row-reverse',
      assistant: 'flex-row',
      tool: 'flex-row',
    },
  },
  defaultVariants: { role: 'assistant' },
})

const avatarStyles = cva(
  'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
  {
    variants: {
      role: {
        user: 'bg-brand-700 text-white',
        assistant: 'bg-gray-100 text-brand-700 border border-gray-200',
        tool: 'bg-amber-100 text-amber-700 border border-amber-200',
      },
    },
    defaultVariants: { role: 'assistant' },
  },
)

const bubbleStyles = cva(
  'relative max-w-[75%] rounded-2xl px-4 py-3 text-body shadow-sm',
  {
    variants: {
      role: {
        user: 'bg-brand-700 text-white rounded-tr-none',
        assistant: 'bg-white border border-gray-200 text-gray-900 rounded-tl-none',
        tool: 'bg-amber-50 border border-amber-200 text-gray-900 rounded-tl-none w-full max-w-full',
      },
    },
    defaultVariants: { role: 'assistant' },
  },
)

export interface ChatBubbleProps extends VariantProps<typeof bubbleStyles> {
  role: 'user' | 'assistant' | 'tool'
  content: string
  senderName?: string
  avatarUrl?: string
  toolName?: string
  toolInput?: unknown
  toolOutput?: unknown
  timestamp?: string
  className?: string
  children?: ReactNode
}

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

/**
 * Assistant turns that triggered tool calls are persisted in the DB as the
 * full Anthropic content-blocks array (JSON string). This extracts only the
 * human-readable text — tool_use blocks have their own `role='tool'` rows
 * which are rendered separately.
 */
function extractAssistantText(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed.startsWith('[')) return raw
  try {
    const parsed: unknown = JSON.parse(trimmed)
    if (!Array.isArray(parsed)) return raw
    const texts: string[] = []
    for (const block of parsed) {
      if (
        block &&
        typeof block === 'object' &&
        (block as { type?: unknown }).type === 'text' &&
        typeof (block as { text?: unknown }).text === 'string'
      ) {
        texts.push((block as { text: string }).text)
      }
    }
    return texts.join('\n').trim()
  } catch {
    return raw
  }
}

function RoleAvatar({ role, avatarUrl }: { role: 'user' | 'assistant' | 'tool'; avatarUrl?: string }) {
  const [imgError, setImgError] = useState(false)
  if (role === 'user' && avatarUrl && !imgError) {
    return (
      <img
        src={avatarUrl}
        alt=""
        className="flex-shrink-0 w-8 h-8 rounded-full object-cover"
        onError={() => setImgError(true)}
      />
    )
  }
  return (
    <div className={avatarStyles({ role })}>
      {role === 'user' && <User className="w-4 h-4" />}
      {role === 'assistant' && <Bot className="w-4 h-4" />}
      {role === 'tool' && <Wrench className="w-4 h-4" />}
    </div>
  )
}

export const ChatBubble = memo(function ChatBubble({
  role,
  content,
  senderName,
  avatarUrl,
  toolName,
  toolInput,
  toolOutput,
  timestamp,
  className,
}: ChatBubbleProps) {
  const [expanded, setExpanded] = useState(false)

  if (role === 'tool') {
    return (
      <div className={cn(bubbleWrapperStyles({ role }), className)}>
        <RoleAvatar role="tool" avatarUrl={undefined} />
        <div className={cn(bubbleStyles({ role }), 'px-3 py-1.5')}>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex items-center justify-between w-full gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded min-w-0"
          >
            <span className="text-caption font-semibold text-amber-800 truncate min-w-0 font-mono">
              {toolName ?? 'unknown'}
            </span>
            {expanded ? (
              <ChevronUp className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            )}
          </button>

          {expanded && (
            <div className="mt-2 space-y-2">
              {toolInput !== undefined && (
                <div>
                  <p className="text-caption font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Input
                  </p>
                  <pre className="text-caption font-mono bg-white border border-amber-200 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap">
                    {formatJson(toolInput)}
                  </pre>
                </div>
              )}
              {toolOutput !== undefined && (
                <div>
                  <p className="text-caption font-semibold text-gray-500 uppercase tracking-wide mb-1">
                    Output
                  </p>
                  <pre className="text-caption font-mono bg-white border border-amber-200 rounded p-2 overflow-auto max-h-48 whitespace-pre-wrap">
                    {formatJson(toolOutput)}
                  </pre>
                </div>
              )}
              {content && (
                <p className="text-small text-gray-700">{content}</p>
              )}
            </div>
          )}
        </div>
      </div>
    )
  }

  const displayText = role === 'assistant' ? extractAssistantText(content) : content

  // Suppress empty assistant bubbles (turn contained only tool_use blocks)
  if (role === 'assistant' && !displayText) return null

  return (
    <div className={cn(bubbleWrapperStyles({ role }), className)}>
      <RoleAvatar role={role} avatarUrl={avatarUrl} />
      <div className={cn('flex flex-col min-w-0 flex-1', role === 'user' ? 'items-end' : 'items-start')}>
        {senderName && (
          <span
            className={cn(
              'text-caption mb-0.5',
              role === 'user' ? 'text-gray-500' : 'text-gray-400',
            )}
          >
            {senderName}
          </span>
        )}
        <div className={cn(bubbleStyles({ role }), timestamp && 'pb-5')}>
        {role === 'assistant' ? (
          <div className={cn(
            // Markdown-Styling: passt zu prose, aber kompakter + an Bubble-Farben angepasst
            'leading-relaxed break-words',
            '[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
            '[&_ul]:my-2 [&_ul]:pl-5 [&_ul]:list-disc',
            '[&_ol]:my-2 [&_ol]:pl-5 [&_ol]:list-decimal',
            '[&_li]:my-0.5',
            '[&_h1]:text-h3 [&_h1]:font-semibold [&_h1]:mt-3 [&_h1]:mb-2',
            '[&_h2]:text-h4 [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1.5',
            '[&_h3]:text-body [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1',
            '[&_strong]:font-semibold',
            '[&_em]:italic',
            '[&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-caption [&_code]:font-mono',
            '[&_pre]:bg-gray-900 [&_pre]:text-gray-100 [&_pre]:p-3 [&_pre]:rounded-md [&_pre]:overflow-x-auto [&_pre]:my-2 [&_pre]:text-caption',
            '[&_pre_code]:bg-transparent [&_pre_code]:text-inherit [&_pre_code]:p-0',
            '[&_blockquote]:border-l-2 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_blockquote]:text-gray-600 [&_blockquote]:my-2',
            '[&_table]:my-2 [&_table]:border-collapse [&_table]:w-full',
            '[&_th]:border [&_th]:border-gray-200 [&_th]:bg-gray-50 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold',
            '[&_td]:border [&_td]:border-gray-200 [&_td]:px-2 [&_td]:py-1',
            '[&_a]:text-brand-600 [&_a]:underline hover:[&_a]:text-brand-700',
            '[&_hr]:my-3 [&_hr]:border-gray-200',
          )}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayText}</ReactMarkdown>
          </div>
        ) : (
          <p className="whitespace-pre-wrap break-words leading-relaxed">{displayText}</p>
        )}
        {timestamp && (
          <span
            className={cn(
              'absolute bottom-1.5 right-3 text-caption leading-none',
              role === 'user' ? 'text-brand-200' : 'text-gray-400',
            )}
          >
            {timestamp}
          </span>
        )}
        </div>
      </div>
    </div>
  )
})
